import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/Add';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { createAdminUser, fetchAdminUsers, updateAdminUserAccess } from '../../services/adminApi';
import { getApiErrorMessage } from '../../services/apiUtils';
import type { AdminUserRecord } from '../../types/admin';
import { useAuth } from '../../hooks/useAuth';
import {
  adminDialogPaperSx,
  adminHeroSx,
  adminMetricIconSx,
  adminSoftChipSx,
  adminSummaryCardSx,
  adminTableCardSx,
} from '../../components/admin/adminVisuals';

const statusColorMap: Record<AdminUserRecord['lifecycleStatus'], 'success' | 'warning' | 'default'> = {
  active: 'success',
  invited: 'warning',
  inactive: 'default',
};

function formatDate(value: string | null) {
  if (!value) return '--';
  return new Date(value).toLocaleString('pt-BR');
}

function formatRoleLabel(role: string) {
  if (!role) return 'Administrador';
  return role === 'admin' ? 'Administrador' : role;
}

export default function AdminUsersPage() {
  const { adminProfile, adminAccessStatus, adminAccessError } = useAuth();
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localInviteUrl, setLocalInviteUrl] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(
    null
  );
  const [form, setForm] = useState({
    email: '',
    fullName: '',
    role: 'admin',
  });

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setFeedback(null);

    try {
      const response = await fetchAdminUsers();
      setUsers(response.users);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Não foi possível carregar os usuários administrativos.');
      setFeedback({ type: 'error', message });
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const summary = useMemo(() => {
    return {
      total: users.length,
      active: users.filter((user) => user.isActive).length,
      invited: users.filter((user) => user.lifecycleStatus === 'invited').length,
      inactive: users.filter((user) => !user.isActive).length,
    };
  }, [users]);

  const handleCreateUser = async () => {
    setIsSubmitting(true);
    setFeedback(null);

    try {
      const response = await createAdminUser(form);
      setUsers((current) =>
        [response.user, ...current].sort((left, right) => {
          if (left.isActive !== right.isActive) return left.isActive ? -1 : 1;
          return left.email.localeCompare(right.email);
        })
      );
      setDialogOpen(false);
      setForm({ email: '', fullName: '', role: 'admin' });
      setLocalInviteUrl(response.invite.setupUrl || null);
      setFeedback({
        type: response.invite.sent ? 'success' : 'info',
        message: response.invite.sent
          ? `Convite enviado com sucesso para ${response.user.email}.`
          : response.invite.setupUrl
            ? `Usuário ${response.user.email} criado. Use o link local abaixo para definir a senha.`
            : `Usuário ${response.user.email} criado, mas o e-mail não foi entregue. Verifique o SMTP e solicite uma nova recuperação de senha.`,
      });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Não foi possível criar o usuário administrativo.');
      setFeedback({ type: 'error', message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleAccess = async (user: AdminUserRecord) => {
    const confirmationMessage = user.isActive
      ? `Bloquear o acesso administrativo de ${user.email}?`
      : `Liberar o acesso administrativo de ${user.email}?`;

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    setFeedback(null);
    setIsSubmitting(true);

    try {
      const response = await updateAdminUserAccess(user.id, !user.isActive);
      setUsers((current) =>
        current
          .map((entry) => (entry.id === user.id ? response.user : entry))
          .sort((left, right) => {
            if (left.isActive !== right.isActive) return left.isActive ? -1 : 1;
            return left.email.localeCompare(right.email);
          })
      );
      setFeedback({
        type: 'success',
        message: response.user.isActive
          ? `Acesso administrativo liberado para ${response.user.email}.`
          : `Acesso administrativo bloqueado para ${response.user.email}.`,
      });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Não foi possível atualizar o acesso administrativo.');
      setFeedback({ type: 'error', message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box sx={adminHeroSx}>
        <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label={adminAccessStatus === 'authorized' ? 'Acesso confirmado' : 'Atenção necessária'}
            variant="outlined"
            sx={adminSoftChipSx}
          />
          <Typography variant="h4" fontWeight={900} sx={{ color: '#16324f' }}>
            Usuários administrativos
          </Typography>
          <Typography sx={{ maxWidth: 860, color: 'rgba(22, 50, 79, 0.88)' }}>
            Crie contas, acompanhe convites e controle quem pode acessar o painel administrativo.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(22, 50, 79, 0.72)' }}>
            Administrador atual: {adminProfile?.email || '--'}
          </Typography>
        </Stack>
      </Box>

      {adminAccessStatus === 'fallback' && (
        <Alert severity="warning">
          {adminAccessError || 'Não foi possível confirmar o acesso completo neste momento.'}
        </Alert>
      )}

      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
      {localInviteUrl && (
        <Alert
          severity="info"
          action={
            <Button component="a" href={localInviteUrl} color="inherit" size="small">
              Abrir link
            </Button>
          }
        >
          O envio por e-mail está desativado neste ambiente local.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Card sx={adminSummaryCardSx}>
            <CardContent>
              <Stack spacing={1.5}>
                <Box sx={adminMetricIconSx('rgba(37, 99, 235, 0.12)', '#1d4ed8')}>
                  <AdminPanelSettingsRoundedIcon />
                </Box>
                <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                  {summary.total}
                </Typography>
                <Typography color="text.secondary">Usuários administrativos cadastrados</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={adminSummaryCardSx}>
            <CardContent>
              <Stack spacing={1.5}>
                <Box sx={adminMetricIconSx('rgba(20, 184, 166, 0.14)', '#0f766e')}>
                  <CheckCircleRoundedIcon />
                </Box>
                <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                  {summary.active}
                </Typography>
                <Typography color="text.secondary">Acessos ativos</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={adminSummaryCardSx}>
            <CardContent>
              <Stack spacing={1.5}>
                <Box sx={adminMetricIconSx('rgba(245, 158, 11, 0.16)', '#b45309')}>
                  <MarkEmailReadRoundedIcon />
                </Box>
                <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                  {summary.invited}
                </Typography>
                <Typography color="text.secondary">Convites pendentes</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={adminSummaryCardSx}>
            <CardContent>
              <Stack spacing={1.5}>
                <Box sx={adminMetricIconSx('rgba(148, 163, 184, 0.18)', '#475569')}>
                  <BlockRoundedIcon />
                </Box>
                <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                  {summary.inactive}
                </Typography>
                <Typography color="text.secondary">Acessos inativos</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={adminTableCardSx}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={900} sx={{ color: '#16324f' }}>
                Controle de acesso
              </Typography>
              <Typography color="text.secondary">
                Crie contas administrativas, acompanhe o status do convite e ative ou desative o acesso ao painel.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Atualizar listagem">
                <span>
                  <IconButton onClick={() => void loadUsers()} disabled={isLoading || isSubmitting}>
                    <RefreshRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)} sx={{ borderRadius: 999 }}>
                Novo usuário
              </Button>
            </Stack>
          </Stack>

          <Alert severity="info" sx={{ mb: 2 }}>
            Ao criar uma conta, a pessoa recebe um e-mail para definir a senha quando o SMTP estiver configurado.
          </Alert>

          <Divider sx={{ mb: 2 }} />

          {isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={22} />
              <Typography color="text.secondary">Carregando usuários administrativos...</Typography>
            </Stack>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Perfil</TableCell>
                    <TableCell>Último acesso</TableCell>
                    <TableCell>Criado em</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography fontWeight={800} sx={{ color: '#16324f' }}>
                            {user.email}
                          </Typography>
                          {user.invitedByEmail && (
                            <Typography variant="caption" color="text.secondary">
                              convidado por {user.invitedByEmail}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{user.fullName || '--'}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            user.lifecycleStatus === 'active'
                              ? 'Ativo'
                              : user.lifecycleStatus === 'invited'
                                ? 'Convite pendente'
                                : 'Inativo'
                          }
                          color={statusColorMap[user.lifecycleStatus]}
                          size="small"
                          variant={user.isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          <Chip label={formatRoleLabel(user.role)} size="small" variant="outlined" />
                          {user.accessMode === 'bootstrap' && (
                            <Chip label="Acesso principal" color="warning" size="small" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDate(user.lastSignInAt)}</TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant={user.isActive ? 'outlined' : 'contained'}
                          color={user.isActive ? 'inherit' : 'primary'}
                          onClick={() => void handleToggleAccess(user)}
                          disabled={isSubmitting}
                          sx={{ borderRadius: 999 }}
                        >
                          {user.isActive ? 'Bloquear' : 'Liberar'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!users.length && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                          Nenhuma conta administrativa foi encontrada.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: adminDialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#16324f' }}>Novo usuário administrativo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="E-mail"
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Nome completo (opcional)"
              value={form.fullName}
              onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Perfil"
              value={form.role}
              onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
              fullWidth
              helperText="Mantenha o perfil padrão, salvo necessidade de organização interna."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateUser()}
            disabled={isSubmitting || !form.email.trim()}
            sx={{ borderRadius: 999 }}
          >
            {isSubmitting ? 'Criando...' : 'Criar usuário'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
